import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

const LOGO_URL = "https://arizonaroses.com/images/arizona-roses-logo.png";

export default function CustomerAccount() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [form, setForm] = useState({ full_name: '', phone: '' });

  useEffect(() => {
    async function fetchProfile() {
      if (!isSupabaseConfigured()) return;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        if (data) {
          setProfile(data);
          setForm({ full_name: data.full_name || '', phone: data.phone || '' });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, []);

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      await supabase
        .from('profiles')
        .update({ full_name: form.full_name, phone: form.phone })
        .eq('id', profile.id);
      setProfile(prev => ({ ...prev, ...form }));
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.hash = '#/';
  };

  if (loading) return (
    <div className="flex justify-center py-20 w-full">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blush-300"></div>
    </div>
  );

  if (!profile) return (
    <div className="flex items-center justify-center min-h-[60vh] px-4">
      <div className="glass-panel p-10 max-w-sm text-center space-y-4">
        <SafeIcon name="User" className="text-5xl text-sage-200 mx-auto" />
        <h2 className="text-2xl font-serif text-charcoal">Not signed in</h2>
        <Link to="/portal" className="btn-primary w-full">Sign In</Link>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-lg mx-auto px-4 py-10 space-y-6"
    >
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="w-20 h-20 rounded-full bg-blush-100 flex items-center justify-center text-blush-400 text-3xl font-bold mx-auto">
          {profile.full_name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <h1 className="text-3xl font-serif text-charcoal">{profile.full_name || 'My Account'}</h1>
        <p className="text-sage-400 text-sm">{profile.email}</p>
      </div>

      {/* Save success banner */}
      {saveSuccess && (
        <div className="bg-green-50 border border-green-100 rounded-2xl p-4 flex items-center gap-3">
          <SafeIcon name="CheckCircle" className="text-green-500 shrink-0" />
          <p className="text-sm text-green-700 font-medium">Your details have been updated.</p>
        </div>
      )}

      {/* Profile Card */}
      <div className="bg-white rounded-[2rem] border border-sage-100 shadow-soft p-6 space-y-5">
        <div className="flex justify-between items-center border-b border-sage-50 pb-3">
          <h2 className="font-serif text-lg text-charcoal">Account Details</h2>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="text-xs text-blush-400 font-bold hover:text-blush-500 transition-colors flex items-center gap-1">
              <SafeIcon name="Pencil" className="text-xs" /> Edit
            </button>
          ) : (
            <button onClick={() => setEditing(false)} className="text-xs text-sage-400 font-bold hover:text-sage-600 transition-colors">
              Cancel
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-4">
            <div className="field-group">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                className="input-field"
                value={form.full_name}
                onChange={(e) => setForm(p => ({ ...p, full_name: e.target.value }))}
              />
            </div>
            <div className="field-group">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                className="input-field"
                value={form.phone}
                onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
              />
            </div>
            <div className="field-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="input-field opacity-60 cursor-not-allowed"
                value={profile.email}
                disabled
              />
              <p className="text-[10px] text-sage-400 mt-1">Email cannot be changed here.</p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary w-full"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <SafeIcon name="User" className="text-blush-300 shrink-0" />
              <div>
                <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Name</p>
                <p className="text-sm font-semibold text-charcoal">{profile.full_name || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SafeIcon name="Mail" className="text-blush-300 shrink-0" />
              <div>
                <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Email</p>
                <p className="text-sm font-semibold text-charcoal">{profile.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SafeIcon name="Phone" className="text-blush-300 shrink-0" />
              <div>
                <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Phone</p>
                <p className="text-sm font-semibold text-charcoal">{profile.phone || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SafeIcon name="Calendar" className="text-blush-300 shrink-0" />
              <div>
                <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Member Since</p>
                <p className="text-sm font-semibold text-charcoal">{new Date(profile.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Place New Order CTA */}
      <Link to="/" className="btn-primary w-full flex items-center justify-center gap-3 shadow-glow">
        <SafeIcon name="Flower2" />
        Place a New Order
      </Link>

      {/* My Orders link */}
      <Link to="/portal" className="btn-secondary w-full flex items-center justify-center gap-3">
        <SafeIcon name="Package" />
        View My Orders
      </Link>

      {/* Sign Out */}
      <button
        onClick={handleSignOut}
        className="w-full flex items-center justify-center gap-2 text-sm text-sage-400 hover:text-red-400 transition-colors py-3 font-semibold"
      >
        <SafeIcon name="LogOut" /> Sign Out
      </button>
    </motion.div>
  );
}
